import * as React from 'react';
import Link from 'next/link';
import { cva, type VariantProps } from 'class-variance-authority';
import { FaBars, FaTimes } from 'react-icons/fa';

import { cn } from '@/lib/utils/cn';

const navbarVariants = cva('sticky top-0 z-50 w-full bg-white backdrop-blur supports-[backdrop-filter]:bg-background/60', {
  variants: {
    variant: {
      default: '',
      transparent: 'bg-transparent border-transparent',
      solid: 'bg-background border-border',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface NavbarProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof navbarVariants> {
  children?: React.ReactNode;
}

function Navbar({ className, variant, children, ...props }: NavbarProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  // Separate floating components from regular navigation items
  const floatingElements = React.Children.toArray(children).filter((child) => React.isValidElement(child) && child.type === NavFloating);
  const navItems = React.Children.toArray(children).filter(
    (child) => !React.isValidElement(child) || (child.type !== NavFloating && child.type !== NavLogo)
  );
  const logo = React.Children.toArray(children).find((child) => React.isValidElement(child) && child.type === NavLogo);

  return (
    <nav className={cn(navbarVariants({ variant }), className)} {...props}>
      <div className="flex h-14 max-w-7xl mx-auto items-center px-1 xl:px-0">
        {logo}
        {/* Desktop Navigation */}
        <div className="flex flex-1 items-center justify-between xl:space-x-1">
          <div className="w-full flex-1 xl:w-auto xl:flex-none">
            <div className="hidden xl:flex xl:space-x-1">{navItems}</div>
          </div>

          {floatingElements.length > 0 && <div className="">{floatingElements}</div>}

          {/* Mobile Menu Button */}
          <button className="xl:hidden p-2" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
            {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="xl:hidden border-t border-border/40 bg-background/95 backdrop-blur shadow-lg">
          <div className="container max-w-7xl mx-auto px-4 py-4 space-y-2">{navItems}</div>
        </div>
      )}
    </nav>
  );
}

export interface NavLogoProps extends React.HTMLAttributes<HTMLAnchorElement> {
  children: React.ReactNode;
  href?: string;
  className?: string;
}

function NavLogo({ children, href = '/', className, ...props }: NavLogoProps) {
  return (
    <div className={cn('mr-1 flex', className)}>
      <Link href={href} className="flex items-center space-x-2 xl:mr-6" {...props}>
        {children}
      </Link>
    </div>
  );
}

export interface NavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href?: string;
  children: React.ReactNode;
  active?: boolean;
}

function NavItem({ className, href, children, active, onClick, ...props }: NavItemProps & { onClick?: React.MouseEventHandler<HTMLAnchorElement> }) {
  return (
    <Link
      href={href || '#'}
      className={cn(
        'flex items-center transition-colors px-2 xl:px-1.5 py-0.5 font-semibold relative',
        !active && 'hover:bg-primary/10 hover:rounded-md',
        active && 'shadow-primary shadow-[0_4px_0_-1px]  cursor-default',
        className
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </Link>
  );
}

export interface NavActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function NavActions({ className, children, ...props }: NavActionsProps) {
  return (
    <div className={cn('flex items-center space-x-2', className)} {...props}>
      {children}
    </div>
  );
}

export interface NavFloatingProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function NavFloating({ className, children, ...props }: NavFloatingProps) {
  return (
    <div className={cn('flex items-center justify-center', className)} {...props}>
      {children}
    </div>
  );
}

Navbar.Item = NavItem;
Navbar.Actions = NavActions;
Navbar.Logo = NavLogo;
Navbar.Floating = NavFloating;

export default Navbar;
export { navbarVariants };
